import React, { useCallback, useEffect, useState } from "react";
import Loading from "../assetLibComponents/Loading";
import Toast from "../assetLibComponents/shared/Toast";
import { getCustomerBranding, updateCustomerBranding } from "../../api/customerSiaApi";
import defaultLogo from "../../assets/logo.png";

const LOGO_ACCEPT = "image/png,image/jpeg,.jpg,.jpeg,.png";

const SettingSiaSetup = ({ authToken }) => {
  const [loading, setLoading] = useState(true);
  const [savingBrand, setSavingBrand] = useState(false);

  const [primaryColor, setPrimaryColor] = useState("");
  const [secondaryColor, setSecondaryColor] = useState("");
  const [disclaimer, setDisclaimer] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [logoFile, setLogoFile] = useState(null);

  const loadBranding = useCallback(async () => {
    const res = await getCustomerBranding(authToken);
    if (res.data?.statusCode === "200" && res.data?.data) {
      const d = res.data.data;
      setPrimaryColor(d.primary_color || "");
      setSecondaryColor(d.secondary_color || "");
      setDisclaimer(d.disclaimer || "");
      setLogoUrl(d.logo || "");
    } else if (res.data?.message) {
      Toast(res.data.message, "E");
    }
  }, [authToken]);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      await loadBranding();
    } catch (e) {
      Toast(e?.response?.data?.message || "Failed to load setup", "E");
    } finally {
      setLoading(false);
    }
  }, [loadBranding]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ok =
      /^image\/(png|jpeg)$/i.test(file.type) ||
      /\.(png|jpg|jpeg)$/i.test(file.name);
    if (!ok) {
      Toast("Please upload PNG or JPG/JPEG only.", "E");
      e.target.value = "";
      return;
    }
    setLogoFile(file);
  };

  const saveBranding = async () => {
    setSavingBrand(true);
    try {
      const res = await updateCustomerBranding(authToken, {
        primary_color: primaryColor,
        secondary_color: secondaryColor,
        disclaimer,
        logoFile,
      });
      if (res.data?.statusCode === "200") {
        Toast(res.data.message || "Branding saved.", "S");
        setLogoFile(null);
        if (res.data?.data?.logo) setLogoUrl(res.data.data.logo);
        await loadBranding();
      } else {
        Toast(res.data?.message || "Save failed", "E");
      }
    } catch (e) {
      Toast(e?.response?.data?.message || "Save failed", "E");
    } finally {
      setSavingBrand(false);
    }
  };

  return (
    <>
      {loading && <Loading />}
      <div className="px-0 max-w-4xl">
        <div className="flex justify-between mb-6 border-b border-gray-200 pb-4">
          <div>
            <div className="text-lg leading-7 font-[500]">Setup</div>
            <p className="text-sm text-gray-500 mt-1">Client branding for SIA</p>
          </div>
        </div>

        <div className="bg-white rounded-md shadow-sm ring-1 ring-gray-200 p-6 mb-8">
          <h2 className="text-base font-semibold text-gray-900 mb-4">
            Branding
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Recommended logo height 70px. Formats: PNG, JPG, JPEG.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">
                Logo
              </label>
              {!logoFile && (
                <div className="mb-2">
                  <img
                    src={logoUrl || defaultLogo}
                    alt={logoUrl ? "Client logo" : "Default logo"}
                    className="max-h-[70px] object-contain"
                  />
                </div>
              )}
              {logoFile && (
                <div className="mb-2">
                  <img
                    src={URL.createObjectURL(logoFile)}
                    alt="New logo preview"
                    className="max-h-[70px] object-contain"
                  />
                </div>
              )}
              <input
                type="file"
                accept={LOGO_ACCEPT}
                onChange={handleLogoChange}
                className="block text-sm text-gray-600"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Primary color (CTAs, key actions)
                </label>
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    value={
                      /^#[0-9A-Fa-f]{6}$/.test(primaryColor)
                        ? primaryColor
                        : "#59B3C6"
                    }
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="h-9 w-14 cursor-pointer rounded border border-gray-300"
                  />
                  <input
                    type="text"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    placeholder="#59B3C6"
                    className="flex-1 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Secondary color (text, icons, supporting UI)
                </label>
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    value={
                      /^#[0-9A-Fa-f]{6}$/.test(secondaryColor)
                        ? secondaryColor
                        : "#000000"
                    }
                    onChange={(e) => setSecondaryColor(e.target.value)}
                    className="h-9 w-14 cursor-pointer rounded border border-gray-300"
                  />
                  <input
                    type="text"
                    value={secondaryColor}
                    onChange={(e) => setSecondaryColor(e.target.value)}
                    placeholder="#000000"
                    className="flex-1 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Disclaimer (shown below the prompt bar in SIA)
              </label>
              <textarea
                rows={3}
                value={disclaimer}
                onChange={(e) => setDisclaimer(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                placeholder="Custom disclaimer text"
              />
            </div>

            <div>
              <button
                type="button"
                disabled={savingBrand}
                onClick={saveBranding}
                className="rounded-md bg-[var(--brand-primary)] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[color-mix(in_srgb,var(--brand-primary)_82%,white)] disabled:opacity-50"
              >
                {savingBrand ? "Saving…" : "Save branding"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SettingSiaSetup;
